import { useEffect } from 'react';
import { useViewer } from './ViewerContext';
import { useViewerStore } from '@/state/viewerStore';

export function useShortcuts(): void {
  const {
    topView,
    resetView,
    hideSelected,
    isolateSelected,
    showAll,
    deselect,
    screenshot,
    zoomToSelection,
    toggleEdges,
    toggleMeasureMode,
    toggleSection,
    deleteSelected,
    undoDelete,
    toggleProjection,
  } = useViewer();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      // undo is the one shortcut that wants a modifier, so it is handled
      // before the guard that drops modified keys
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undoDelete();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const hasModel = useViewerStore.getState().modelName !== null;
      const hasSelection = useViewerStore.getState().selection.expressID !== null;

      switch (e.key.toLowerCase()) {
        case 'f':
          // zooms to the selection, or fits the whole model without one
          if (hasModel) {
            e.preventDefault();
            zoomToSelection();
          }
          break;
        case 'e':
          if (hasModel) {
            e.preventDefault();
            toggleEdges();
          }
          break;
        case 'b':
          if (hasModel) {
            e.preventDefault();
            const store = useViewerStore.getState();
            store.setTreeOpen(!store.treeOpen);
          }
          break;
        case 'm':
          if (hasModel) {
            e.preventDefault();
            toggleMeasureMode();
          }
          break;
        case 'x':
          if (hasModel) {
            e.preventDefault();
            toggleSection();
          }
          break;
        case 'o':
          if (hasModel) {
            e.preventDefault();
            toggleProjection();
          }
          break;
        case 't':
          if (hasModel) {
            e.preventDefault();
            topView();
          }
          break;
        case 'r':
          if (hasModel) {
            e.preventDefault();
            resetView();
          }
          break;
        case 'h':
          if (e.shiftKey) {
            e.preventDefault();
            showAll();
          } else if (hasSelection) {
            e.preventDefault();
            hideSelected();
          }
          break;
        case 'i':
          if (hasSelection) {
            e.preventDefault();
            isolateSelected();
          }
          break;
        case 's':
          if (hasModel) {
            e.preventDefault();
            void screenshot();
          }
          break;
        case 'delete':
        // Mac keyboards send Backspace where a full keyboard sends Delete
        case 'backspace':
          if (hasSelection) {
            e.preventDefault();
            deleteSelected();
          }
          break;
        case 'escape':
          if (useViewerStore.getState().measureMode) {
            e.preventDefault();
            toggleMeasureMode();
          } else if (hasSelection) {
            e.preventDefault();
            deselect();
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    topView,
    resetView,
    hideSelected,
    isolateSelected,
    showAll,
    deselect,
    screenshot,
    zoomToSelection,
    toggleEdges,
    toggleMeasureMode,
    toggleSection,
    deleteSelected,
    undoDelete,
    toggleProjection,
  ]);
}
